import { useMemo, useState } from 'react';
import { BrandSpinner, GrowthChart } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { GrowthChartPrintDialog } from '@/encounter/growth-chart-print-dialog';
import { isGrowthChartAge, toGrowthSeries, toWeightForLength } from '@/encounter/lib/to-growth-series';
import { useVitalsHistory } from '@/encounter/services/use-vitals-history';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/**
 * The pediatric growth chart under the scheduling Vitals tab, behind `?x=growth-chart`. Flutter
 * mounts `GrowthChartGraph` after the vitals grid (`vitals_tab.dart:320`) with the same gate the
 * pre-encounter uses: age <= 19 (`growth_chart.dart:1218`) and a sex on file. The DS chart carries
 * Flutter's age-band tabs and the cm/in switch; the print icon opens the same sheet as the
 * pre-encounter (`GrowthChartPrintDialog`), shown only when there is a practice to print for.
 */
export interface VitalsGrowthChartProps {
  patientId: string | null | undefined;
  ageYears: number | null | undefined;
  /** The display age, printed verbatim ("1y 7m 26d"). */
  ageDisplay?: string | null;
  sex: 'male' | 'female' | null;
  /** The raw birth date the readings' ages are taken from. */
  birthDate?: string | null;
  patientName?: string | null;
  mrn?: string | null;
}

/** The preload patient (`EditPatientResponse` via Practice.Aggregator PreLoadAppointmentDetails). */
export interface PreloadGrowthPatient {
  age?: string;
  birthDate?: string;
  dateOfBirth?: string;
  name?: string | null;
  firstName?: string;
  lastName?: string;
  /** The preload sends the MRN as `patientMrn`; `mrn` is the slim participant shape. */
  patientMrn?: string;
  mrn?: string;
}

/** Birth date, display name and MRN the chart and its print dialog need, from the preload patient. */
export function growthChartPatientFrom(
  p: PreloadGrowthPatient | undefined,
): Pick<VitalsGrowthChartProps, 'ageDisplay' | 'birthDate' | 'patientName' | 'mrn'> {
  return {
    ageDisplay: p?.age,
    birthDate: p?.birthDate ?? p?.dateOfBirth,
    patientName: p?.name ?? ([p?.firstName, p?.lastName].filter(Boolean).join(' ') || null),
    mrn: p?.patientMrn ?? p?.mrn,
  };
}

export function VitalsGrowthChart(props: VitalsGrowthChartProps) {
  const { ageYears, sex, birthDate } = props;
  // Gate BEFORE any query hook: with the switch off (or nothing to draw) the tab runs no growth
  // fetch and needs no QueryClient for it. Without a birth date there is no age to plot at.
  if (!isEncounterFlagOn('growthChart') || !isGrowthChartAge(ageYears) || !sex || !birthDate) return null;
  return <GrowthChartSection {...props} sex={sex} birthDate={birthDate} />;
}

function GrowthChartSection({
  patientId,
  ageYears,
  ageDisplay,
  sex,
  birthDate,
  patientName,
  mrn,
}: VitalsGrowthChartProps & { sex: 'male' | 'female'; birthDate: string }) {
  const authUser = useAuthStore((s) => s.user);
  const history = useVitalsHistory(patientId, true);
  const [printOpen, setPrintOpen] = useState(false);
  const series = useMemo(() => toGrowthSeries(history.byCode, birthDate), [history.byCode, birthDate]);
  const weightForLength = useMemo(() => toWeightForLength(history.byCode), [history.byCode]);

  const chart = { series, weightForLength, ageYears: ageYears ?? undefined, sex };
  const canPrint = !!authUser?.practiceId && !!authUser?.id;
  return (
    <section aria-label="Growth chart" data-slot="appt-growth-chart" className="mt-4">
      {/* A spinner while the history loads, never an empty chart saying "No readings". */}
      {history.isLoading ? (
        <BrandSpinner size={20} label="Loading growth chart…" />
      ) : history.isError ? (
        // A failed read is not "no readings": say so instead of drawing an empty chart.
        <p className="text-sm text-status-error">Couldn't load the growth chart.</p>
      ) : (
        <GrowthChart {...chart} onPrint={canPrint ? () => setPrintOpen(true) : undefined} />
      )}
      {canPrint ? (
        <GrowthChartPrintDialog
          open={printOpen}
          onClose={() => setPrintOpen(false)}
          practitionerId={authUser?.id}
          practiceId={authUser?.practiceId}
          patient={{
            fullName: patientName,
            mrn,
            genderName: sex === 'female' ? 'Female' : 'Male',
            age: ageDisplay,
          }}
          chart={chart}
        />
      ) : null}
    </section>
  );
}
